import { CaserPlanSelected } from '../types/enums';

/**
 * Documentación y códigos regulatorios de cada plan de empleo comercializado.
 *
 * Fuente única de verdad del front: antes vivían duplicados y hardcodeados en
 * la vista de partícipe (`PlanEmpleoV2`) y en la de admin (`ConfigurarBeneficioAdminV2`),
 * apuntando a los planes INDIVIDUALES (N5138 / N5137), que no son estos.
 *
 * Los códigos DGSFP y de fondo están verificados contra `pension_plan_registry`
 * (comparsa, `scripts/seed_pension_plan_registry.sql`) y contra el contenido real
 * de los PDFs.
 *
 * Provisional por diseño: el destino de estos datos es `pension_plan_catalog`,
 * para poder actualizarlos sin desplegar. Ver la tarea de Discovery
 * "[B2B] Documentos del plan dinámicos desde pension_plan_catalog".
 */
export interface EmploymentPlanDocsI {
	/** Nº de registro DGSFP del PLAN (no del fondo) */
	dgsfpCode: string;
	/**
	 * Nombre del plan en el catálogo de comparsa (`pension_plan_catalog.name`): con él se pide su
	 * rentabilidad a `/plan-returns/arca-plans` (chirigota#576), la misma que enseñan los funnels.
	 * Por su código DGSFP, un plan reciente solo tendría su propio dato a 1 año.
	 */
	catalogSlug: string;
	/** Nº de registro DGSFP del fondo de renta variable */
	stocksFundCode: string;
	/** Nº de registro DGSFP del fondo de renta fija; null si el plan no tiene */
	bondsFundCode: string | null;
	/** Datos Fundamentales para el Partícipe, renta variable. Vacío = aún no disponible */
	dfpStocksUrl: string;
	/** Datos Fundamentales para el Partícipe, renta fija; null si el plan no tiene */
	dfpBondsUrl: string | null;
	/** Informe periódico de la gestora, renta variable */
	managerReportStocksUrl: string;
	/** Informe periódico de la gestora, renta fija; null si el plan no tiene */
	managerReportBondsUrl: string | null;
}

export const EMPLOYMENT_PLAN_DOCS: Record<CaserPlanSelected, EmploymentPlanDocsI> = {
	/** Indexa Empleo (ciclo de vida): dos fondos bajo un único plan N5375 */
	[CaserPlanSelected.EMPLOYMENT_LIFE_CYCLES]: {
		dgsfpCode: 'N5375',
		catalogSlug: 'indexa-empleo-ppe',
		stocksFundCode: 'F2119',
		bondsFundCode: 'F2118',
		dfpStocksUrl: 'https://drive.google.com/file/d/13XFosbKchZpTAHvYHoyQw9d56l911ZBu/view',
		dfpBondsUrl: 'https://drive.google.com/file/d/1CDQYuLp6CHv3IuF3RYMTdZWEHouUqqGr/view',
		managerReportStocksUrl:
			'https://drive.google.com/file/d/189-5ONDMcJKSbOBp_q8w980k8_oVp9tS/view?usp=sharing',
		managerReportBondsUrl:
			'https://drive.google.com/file/d/1wJldePi6JEIWCqSEXA-j31CsWNL-xBsc/view?usp=sharing'
	},
	/**
	 * Indexa Empleo 100% Acciones: un único fondo (el mismo F2119 del ciclo de
	 * vida) bajo un plan distinto, N5509. Que compartan fondo no los hace
	 * intercambiables: el documento de N5375 declara otra cuenta de posición
	 * (5.044.240,30 € frente a 1.895.348,03 €) y otra fecha de inicio de plan.
	 */
	[CaserPlanSelected.EMPLOYMENT_100_ACTIONS]: {
		dgsfpCode: 'N5509',
		catalogSlug: 'indexa-empleo-100-acciones-ppe',
		stocksFundCode: 'F2119',
		bondsFundCode: null,
		dfpStocksUrl: 'https://drive.google.com/file/d/1UDchfgSpdHBrTynHHN4aIUUJ-aVEZv5i/view',
		dfpBondsUrl: null,
		managerReportStocksUrl:
			'https://drive.google.com/file/d/1rAi9V8j3j4L2oIVxkdxCigQWej7eM8Bn/view?usp=sharing',
		managerReportBondsUrl: null
	}
};

/**
 * Resuelve los documentos del plan de empleo de una empresa a partir de su
 * `company_epp_config.caser_plan_selected`.
 *
 * Fallback a 100% acciones cuando el valor falta o es desconocido: es el plan
 * de un solo fondo, así que en el peor caso se muestran menos documentos, nunca
 * los de un fondo que la empresa no tiene. Ojo: los CÓDIGOS del fallback (N5509)
 * sí se pintan como si fueran los suyos — con el dato bien poblado en BBDD esta
 * rama no se alcanza, pero no es una salida silenciosa del todo.
 *
 * El acceso va por `hasOwnProperty` a propósito: indexar directamente con una
 * clave heredada (`'constructor'`, `'toString'`) devuelve un miembro de
 * `Object.prototype`, que es truthy, así que el `??` no saltaría y el plan
 * saldría con todos sus campos a `undefined`.
 */
export const getEmploymentPlanDocs = (caserPlanSelected?: string | null): EmploymentPlanDocsI => {
	const isKnownPlan =
		typeof caserPlanSelected === 'string' &&
		Object.prototype.hasOwnProperty.call(EMPLOYMENT_PLAN_DOCS, caserPlanSelected);

	return isKnownPlan
		? EMPLOYMENT_PLAN_DOCS[caserPlanSelected as CaserPlanSelected]
		: EMPLOYMENT_PLAN_DOCS[CaserPlanSelected.EMPLOYMENT_100_ACTIONS];
};

/**
 * Nombre en el catálogo de comparsa del plan de empleo contratado, o `null` si la empresa no
 * tiene plan o llega un valor desconocido (no se cae al plan por defecto: la rentabilidad de
 * otro plan sería engañosa; chirigota#576).
 */
export const employmentPlanCatalogSlug = (caserPlanSelected?: string | null): string | null =>
	typeof caserPlanSelected === 'string' &&
	Object.prototype.hasOwnProperty.call(EMPLOYMENT_PLAN_DOCS, caserPlanSelected)
		? EMPLOYMENT_PLAN_DOCS[caserPlanSelected as CaserPlanSelected].catalogSlug
		: null;
